import { useAuth } from '@/context/AuthContext';
import { useSiteConfig } from '@/context/SiteConfigContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';

/**
 * Componente que valida se o usuário tem permissão para acessar a rota atual.
 * Se não tiver permissão, redireciona para a landing page.
 */
export default function ValidationGuard({
    children,
}: {
    children: React.ReactNode;
}) {
    const { isAuthenticated, user, isLoading: authLoading } = useAuth();
    const { requireVipToAccess, loading: configLoading } = useSiteConfig();
    const location = useLocation();
    const pathname = location.pathname;
    const navigate = useNavigate();

    const isInternalPage = pathname !== '/landing' && pathname !== '/termos';
    const isProfilePage = pathname === '/perfil' || pathname.startsWith('/perfil/') || pathname === '/meu-perfil';
    const isPlanosPage = pathname === '/planos';
    const isAdmin = !!user?.usr_admin;
    const isLoading = authLoading || configLoading;

    useEffect(() => {
        if (!isLoading && isInternalPage) {
            // Se não estiver autenticado e tentar acessar página interna
            if (!isAuthenticated) {
                const search = typeof window !== 'undefined' ? window.location.search : '';
                navigate(`/landing${search}`, { replace: true });
                return;
            }

            // Se o site exige VIP e o usuário não for VIP (apenas para páginas internas, exceto perfil e planos)
            if (requireVipToAccess && !user?.is_vip && !isProfilePage && !isPlanosPage && !isAdmin) {
                const search = typeof window !== 'undefined' ? window.location.search : '';
                navigate(`/landing${search}`, { replace: true });
                return;
            }
        }
    }, [isLoading, isAuthenticated, user, pathname, navigate, requireVipToAccess, isInternalPage, isProfilePage]);

    // Não renderiza o conteúdo protegido se ainda estiver validando
    if (isInternalPage && (isLoading || !isAuthenticated || (requireVipToAccess && !user?.is_vip && !isProfilePage && !isPlanosPage && !isAdmin))) {
        return null;
    }

    return <>{children}</>;
}
